// "Sync now" + live progress for the reviews screens (admin, seller, tracking
// settings). The sync runs in the background on the server: the trigger returns
// at once and this control polls the summary, which carries the progress — the
// store being synced, n of total, and the stores that could not be synced.
// When a running sync ends, the review lists refresh on their own.

import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useT } from '../../i18n'
import { reviewsI18n } from '../../i18n/reviews'
import {
  fetchAdminReviewsSummary,
  fetchSellerReviewsSummary,
  syncSellerReviews,
  triggerReviewSync,
} from '../../api/reviews'
import type { ReviewSummary } from '../../api/reviews.types'
import { Button } from '../../components/ui/button'
import { useToastStore } from '../../lib/toast'
import { formatRelative } from '../../lib/datetime'
import { syncPollInterval } from './ReviewSyncControl.lib'

type Scope = 'admin' | 'seller'

// Same keys the review pages use for their unfiltered summary, so the page's
// summary cards and this control share one cache entry and one poll.
const SUMMARY_KEY: Record<Scope, readonly unknown[]> = {
  admin: ['admin', 'reviews', 'summary', {}],
  seller: ['seller', 'reviews', 'summary'],
}

const INVALIDATE_KEYS: Record<Scope, readonly (readonly unknown[])[]> = {
  admin: [['admin', 'reviews'], ['admin', 'review-tracking-settings']],
  seller: [['seller', 'reviews']],
}

export function ReviewSyncControl({ scope }: { scope: Scope }) {
  const t = useT(reviewsI18n)
  const qc = useQueryClient()
  const pushToast = useToastStore((s) => s.push)
  const summaryKey = SUMMARY_KEY[scope]

  const summary = useQuery({
    queryKey: summaryKey,
    queryFn: () =>
      scope === 'admin' ? fetchAdminReviewsSummary() : fetchSellerReviewsSummary(),
    refetchInterval: (query) => syncPollInterval(query.state.data),
  })

  const progress = summary.data?.sync_progress ?? null
  const running = progress?.running ?? false

  const sync = useMutation({
    mutationFn: () => (scope === 'admin' ? triggerReviewSync() : syncSellerReviews()),
    onSuccess: (res) => {
      qc.setQueryData<ReviewSummary>(summaryKey, (old) =>
        old ? { ...old, sync_progress: res.progress ?? old.sync_progress } : old,
      )
      void qc.invalidateQueries({ queryKey: summaryKey, exact: true })
      if (!res.started) pushToast({ variant: 'info', title: t.syncAlreadyRunning })
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : String(e)
      const isCooldown = /cooldown/i.test(message)
      pushToast({
        variant: isCooldown ? 'warning' : 'error',
        title: isCooldown ? t.syncCooldown : t.tracking.syncError,
        description: isCooldown ? undefined : message,
      })
    },
  })

  // A running sync just ended → refresh the lists and say how it went.
  const wasRunning = useRef(running)
  useEffect(() => {
    if (wasRunning.current && !running) {
      for (const key of INVALIDATE_KEYS[scope]) void qc.invalidateQueries({ queryKey: key })
      const failed = progress?.failed_stores.length ?? 0
      pushToast(
        failed > 0
          ? { variant: 'warning', title: t.syncFinishedWithFailures(failed) }
          : { variant: 'success', title: t.syncFinished },
      )
    }
    wasRunning.current = running
  }, [running, progress, scope, qc, pushToast, t])

  const busy = running || sync.isPending
  const lastSyncedAt = summary.data?.last_synced_at ?? null
  const failedStores = !running ? (progress?.failed_stores ?? []) : []

  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="primary" size="sm" disabled={busy} onClick={() => sync.mutate()}>
        <RefreshCw className={'mr-1 h-4 w-4' + (busy ? ' animate-spin' : '')} />
        {busy ? t.syncing : t.syncNow}
      </Button>
      {running && progress ? (
        <div className="text-right text-xs text-slate-500" role="status" aria-live="polite">
          <div>
            {progress.total_stores === 0
              ? t.syncStarting
              : t.syncProgress(progress.done_stores, progress.total_stores)}
          </div>
          {progress.current_store ? (
            <div className="font-medium text-slate-700">
              {t.syncCurrent(progress.current_store)}
            </div>
          ) : null}
        </div>
      ) : (
        <span className="text-xs text-slate-400">
          {lastSyncedAt ? t.lastSynced(formatRelative(lastSyncedAt)) : t.neverSynced}
        </span>
      )}
      {failedStores.length > 0 ? (
        <span className="max-w-xs text-right text-xs text-amber-700">
          {t.syncFailedStores(failedStores.join(', '))}
        </span>
      ) : null}
    </div>
  )
}
